/**
 * AST walker that strips `indexSignatures` from every `Objects` node in a
 * Schema's AST, preserving all `propertySignatures` (including their
 * transforms/checks/annotations).
 *
 * # Why this exists
 *
 * The openapi-gen in `packages/openapi-codegen` (`@vs-shop/openapi-codegen`) emits
 * every API shape as
 * `Schema.StructWithRest(Schema.Struct({...}), [Schema.Record(Schema.String, Schema.Unknown)])`.
 * The rest record preserves every unknown backend field during decoding, but
 * storefront response schemas intentionally expose only their named contract.
 *
 * `withoutRest` walks the AST and drops `indexSignatures` from every `Objects`
 * node. The named property set, transforms, checks, encodings, annotations, and
 * parent context survive untouched. Effect beta.98 fixed the earlier
 * `StructWithRest` field-transform bug; this helper remains responsible only
 * for removing catch-all fields from decoded storefront values.
 *
 * # Trade-off
 *
 * The `[x: string]: unknown` index signature is dropped from the decoded
 * `Type`. A grep of the rcprofi codebase at the time of writing found zero
 * consumers that read dynamic properties off a decoded openapi-typed value
 * (no `data['unknownField']`, no `Object.keys` over decoded bodies). If a
 * future caller needs that index signature back, wrap the consumed schema
 * locally with the original `StructWithRest` (just don't pass it through
 * `schemaBodyJsonClean`).
 */

import { Schema, SchemaAST } from 'effect';

const replaceEncoding = <A extends SchemaAST.AST>(
  ast: A,
  encoding: SchemaAST.Encoding | undefined
): A => {
  if (ast.encoding === encoding) return ast;

  const descriptors = Object.getOwnPropertyDescriptors(ast);
  const encodingDescriptor = descriptors.encoding;
  if (encodingDescriptor === undefined) return ast;

  return Object.create(Object.getPrototypeOf(ast), {
    ...descriptors,
    encoding: { ...encodingDescriptor, value: encoding },
  });
};

// Walk the encoding chain too. `Schema.decodeTo` wraps the target's AST and
// stores the original (from) AST inside `encoding[*].to` — the suffix is
// confusing but `Link.to` literally holds the *upstream* AST in the decode
// direction. If we leave those untouched, a `withoutRest(withoutJsonLd(X))`
// walk strips the outer (type-side) Objects but not X.ast (which is the one
// the decoder actually runs against to materialise the Type).
const stripEncoding = (
  encoding: SchemaAST.Encoding | undefined
): SchemaAST.Encoding | undefined => {
  if (encoding === undefined) return undefined;
  const [first, ...rest] = encoding;
  return [
    new SchemaAST.Link(stripIndexSignaturesFromAST(first.to), first.transformation),
    ...rest.map(
      (link) => new SchemaAST.Link(stripIndexSignaturesFromAST(link.to), link.transformation)
    ),
  ];
};

const stripIndexSignaturesFromAST = (ast: SchemaAST.AST): SchemaAST.AST => {
  switch (ast._tag) {
    case 'Objects': {
      const processedProps = ast.propertySignatures.map(
        (ps) => new SchemaAST.PropertySignature(ps.name, stripIndexSignaturesFromAST(ps.type))
      );
      return new SchemaAST.Objects(
        processedProps,
        // Drop the index signatures entirely. Preserve everything else.
        [],
        ast.annotations,
        ast.checks,
        stripEncoding(ast.encoding),
        ast.context
      );
    }

    case 'Union':
      return new SchemaAST.Union(
        ast.types.map(stripIndexSignaturesFromAST),
        ast.options,
        ast.annotations,
        ast.checks,
        stripEncoding(ast.encoding),
        ast.context,
        ast.encodingChecks
      );

    case 'Arrays':
      return new SchemaAST.Arrays(
        ast.isMutable,
        ast.elements.map(stripIndexSignaturesFromAST),
        ast.rest.map(stripIndexSignaturesFromAST),
        ast.annotations,
        ast.checks,
        stripEncoding(ast.encoding),
        ast.context
      );

    case 'Suspend':
      // Defer the thunk so recursive schemas still typecheck; the wrapper
      // calls `stripIndexSignaturesFromAST` lazily when the thunk is forced.
      return new SchemaAST.Suspend(
        () => stripIndexSignaturesFromAST(ast.thunk()),
        ast.annotations,
        ast.checks,
        stripEncoding(ast.encoding),
        ast.context
      );

    case 'Declaration':
      // A `Declaration` carries type parameters (e.g. `Schema.Array(T)`). We
      // recurse into them so nested Objects inside the parameters get
      // rewritten, but leave `run` (the parser) intact — it's closed over
      // the original parameters.
      return new SchemaAST.Declaration(
        ast.typeParameters.map(stripIndexSignaturesFromAST),
        ast.run,
        ast.annotations,
        ast.checks,
        stripEncoding(ast.encoding),
        ast.context
      );

    default:
      // For terminal nodes (String, Number, Literal, Null, …) we still need
      // to walk the encoding chain since a primitive like `Schema.URL`
      // carries the string→URL transform there.
      return replaceEncoding(ast, stripEncoding(ast.encoding));
  }
};

/**
 * Strip `indexSignatures` from every `Objects` node reachable from `self`'s
 * AST. The result is a schema with identical named-property semantics but no
 * rest record, so unknown backend fields are not retained in decoded values.
 */
export const withoutRest = <S extends Schema.Top>(
  self: S
): Schema.Codec<S['Type'], S['Encoded'], never, never> => {
  return Schema.make<Schema.Codec<S['Type'], S['Encoded'], never, never>>(
    stripIndexSignaturesFromAST(self.ast)
  );
};
